/**
 * OTOMATİK KİLİT (kullanıcı kararları K1, D2): son etkileşimden sonra seçilen
 * süre (5/15/30/60 dk, varsayılan 15) dolunca bekleyen kap yazımı bitirilir
 * ve sayfa yeniden yüklenir — Worker ölür, ana anahtar bellekten gider
 * (taslak §1.3: kilit = yeniden yükleme). Sekme gizlenince yazım beklemeden
 * yapılır (Ç0: kayıp penceresi).
 *
 * KISA SÜRELİ OTURUM (kullanıcı kararı 12.10.2026): açıkken anahtar aynı
 * süre için tarayıcıda (dışarı okunamayan anahtarla sarılı) saklanır; süre
 * etkinlikle kayar (en çok dakikada bir yenilenir). Bu süre içinde sayfa
 * yeniden yüklenirse parola sorulmaz. "Kilitle" ve otomatik kilit önce
 * saklanan oturumu siler — kilit yine kilittir.
 *
 * GİZLİLİK PERDESİ (kullanıcı kararları 29.10.2026): kasa açıkken sayfa
 * gizlenince (başka uygulama/sekme, küçültme, ekran kilidi) üstüne düz
 * zeminli, logolu bir perde iner — telefonun uygulama değiştiricisindeki
 * anlık görüntüde ve dönüşün ilk anında değer görünmesin. Perde DOM'a
 * doğrudan, aynı olayda yazılır (React çizimini beklemez). Dönüşte önce
 * kilit süresine bakılır: gizliyken zamanlayıcılar durdurulabildiği için
 * süre son etkileşimden saatle ölçülür; dolduysa perde kalkmadan kilitlenir.
 * Masaüstünde yalnızca başka pencereye geçmek (sayfa görünürken) perde
 * indirmez. Dokunmatik cihazda (iPhone) ise odak kaybında da iner: uygulama
 * değiştiricide uygulama yalnızca "etkin değil" olur, sayfa gizlenmez ve
 * önizleme o anda alınır (kullanıcı bildirimi 01.11.2026, iOS Brave). Bileşen yalnızca kasa açıkken çizilir: tanıtım ve kilit
 * ekranında perde yok.
 *
 * HAREKETSİZLİKTE PERDE (kullanıcı kararları 02.11.2026): sayfa görünürken
 * de seçilen süre (cihazda; varsayılan 60 sn) dokunulmazsa perde iner,
 * ilk dokunuşla kalkar — parola sorulmaz, kilit süresi ayrıca işler.
 * Perde her indiğinde, seçildiyse tutarlar gizlenir (`lib/tutarGizleme`).
 *
 * KİLİT KARARI SAATLE (03.11.2026, telefon bildirimi): iOS ekran kilidinden
 * dönüşte odak sinyali gelmeyebiliyor; o zaman ilk dokunuş perdeyi kilit
 * süresine bakmadan kaldırıyordu. Artık her etkinlik ve "sayfa geri geldi"
 * (`pageshow`, `resume`) önce son etkinlikten geçen süreyi saatle ölçer.
 */

import { useEffect, useRef, useState } from "react";
import {
  KILIT_SURELERI_DK,
  VARSAYILAN_KILIT_SURESI_DK,
  type KilitPort,
  type KilitSuresiDk,
} from "@parafon/contracts/local-api";
import { useRepositoryContext } from "../../repo/RepositoryContext";
import { kapiOturumunuKapat } from "../../kabiliyet/KabiliyetSaglayici";
import { M } from "../../dil";
import { taniAcik, taniYaz } from "../../lib/perdeTanisi";
import { perdedeGizlemeyiUygula, perdeSuresi } from "../../lib/tutarGizleme";

export const KILIT_SURESI_AYARI = "kilit.sureDk";

export function kilitSuresi(deger: string | null): KilitSuresiDk {
  const n = Number(deger);
  return (KILIT_SURELERI_DK as readonly number[]).includes(n) ? (n as KilitSuresiDk) : VARSAYILAN_KILIT_SURESI_DK;
}

/** Şimdi kilitle: önce bekleyen yazım (hata olsa da kilitlenir — durum bandı söyledi), ücretli oturumu kapat, sonra yeniden yükle. */
export async function kilitle(kilit: KilitPort, yenidenYukle: () => void): Promise<void> {
  try {
    await kilit.yaz();
  } catch {
    /* yazım hatası zaten bantta; kilitlemeyi engellemez */
  }
  // Ücretli oturum kapıya bildirilir (oturum sınırında yer açılsın); en çok
  // bir buçuk saniye beklenir, ulaşılamazsa oturum bir saatte kendiliğinden düşer.
  await kapiOturumunuKapat();
  try {
    await kilit.oturumuUnut?.();
  } catch {
    /* silinemezse süresi dolunca kendiliğinden geçersiz olur */
  }
  yenidenYukle();
}

/** Saklanan oturumun süresi en çok bu aralıkta bir yenilenir (etkinlikte). */
export const OTURUM_YENILEME_MS = 60_000;

/** Yeniden yüklemeden sonra kilit ekranı yerine "yeni profil" kurulumunu aç (sekme başına). */
const YENI_PROFIL_ISARETI = "parafon.yeniProfil";

/** Ayarlar'dan "Yeni profil ekle": açık profil kilitlenir, kurulum ekranı gelir. */
export async function yeniProfilIcinKilitle(kilit: KilitPort, yenidenYukle: () => void): Promise<void> {
  try {
    globalThis.sessionStorage?.setItem(YENI_PROFIL_ISARETI, "1");
  } catch {
    /* depolama kapalıysa kilit ekranındaki "Yeni profil ekle" yine var */
  }
  await kilitle(kilit, yenidenYukle);
}

/** İşaret bir kez okunur ve silinir. */
export function yeniProfilIstendiMi(): boolean {
  try {
    const v = globalThis.sessionStorage?.getItem(YENI_PROFIL_ISARETI) === "1";
    globalThis.sessionStorage?.removeItem(YENI_PROFIL_ISARETI);
    return v;
  } catch {
    return false;
  }
}

const ETKILESIM = ["pointerdown", "keydown", "wheel", "touchstart"] as const;

/** Perde başta kapalı; görünürlüğü yalnızca olaylarda doğrudan yazılır (sabit nesne: React yeniden yazmaz). */
const PERDE_KAPALI = { display: "none" } as const;

export function OtomatikKilit({
  yenidenYukle = () => globalThis.location?.reload(),
}: {
  readonly yenidenYukle?: () => void;
}) {
  const { repo, revision } = useRepositoryContext();
  const kilit = repo.kilit;
  const [sureDk, setSureDk] = useState<KilitSuresiDk>(VARSAYILAN_KILIT_SURESI_DK);
  // Her çizimde yeni bir işlev gelse de zamanlayıcı baştan kurulmasın.
  const yukle = useRef(yenidenYukle);
  yukle.current = yenidenYukle;
  const perde = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (kilit === undefined) return;
    let iptal = false;
    void repo.settings.get(KILIT_SURESI_AYARI).then(
      (v) => {
        if (!iptal) setSureDk(kilitSuresi(v));
      },
      () => undefined,
    );
    return () => {
      iptal = true;
    };
  }, [repo, kilit, revision]);

  useEffect(() => {
    if (kilit === undefined) return;
    let zaman: ReturnType<typeof setTimeout> | null = null;
    let bosZaman: ReturnType<typeof setTimeout> | null = null;
    // Perde hareketsizlikten mi indi (yalnızca o perde dokunuşla kalkar).
    let bosPerde = false;
    let sonYenileme = Number.NEGATIVE_INFINITY;
    let sonEtkinlik = Date.now();
    let kilitleniyor = false;
    const perdeyiAyarla = (acik: boolean): void => {
      if (perde.current !== null) perde.current.style.display = acik ? "flex" : "none";
      if (acik) perdedeGizlemeyiUygula();
      else bosPerde = false;
    };
    const kilitleSimdi = (): void => {
      if (kilitleniyor) return;
      kilitleniyor = true;
      perdeyiAyarla(true);
      void kilitle(kilit, () => yukle.current());
    };
    const surdur = (): void => {
      const simdi = Date.now();
      if (simdi - sonYenileme < OTURUM_YENILEME_MS) return;
      sonYenileme = simdi;
      void kilit.oturumuSurdur?.(sureDk).catch(() => undefined);
    };
    const bosKur = (): void => {
      if (bosZaman !== null) clearTimeout(bosZaman);
      // Her seferinde okunur: Ayarlar'daki değişiklik yeniden yüklemeden geçerli.
      const perdeSn = perdeSuresi();
      bosZaman =
        perdeSn === 0
          ? null
          : setTimeout(() => {
              if (perde.current?.style.display === "flex") return;
              perdeyiAyarla(true);
              bosPerde = true;
            }, perdeSn * 1_000);
    };
    const kur = (): void => {
      if (kilitleniyor) return;
      // Önce saat (03.11.2026, telefon): dönüş sinyali hiç gelmediyse ilk
      // dokunuş süreyi sıfırlamadan önce kilit süresini denetler.
      if (Date.now() - sonEtkinlik >= sureDk * 60_000) {
        kilitleSimdi();
        return;
      }
      if (zaman !== null) clearTimeout(zaman);
      sonEtkinlik = Date.now();
      zaman = setTimeout(kilitleSimdi, sureDk * 60_000);
      if (bosPerde && !kilitleniyor) perdeyiAyarla(false);
      bosKur();
      surdur();
    };
    const tani = taniAcik();
    const dokunmatik = globalThis.matchMedia?.("(pointer: coarse)").matches ?? false;
    // Dönüş: süre dolduysa perde kalkmadan kilit; dolmadıysa perde kalkar.
    const donus = (): void => {
      const gecen = Date.now() - sonEtkinlik;
      const perdeSn = perdeSuresi();
      if (gecen >= sureDk * 60_000) kilitleSimdi();
      else if (kilitleniyor) return;
      // Hareketsizlik süresi de dolduysa perde dokunuşa kadar kalır.
      else if (bosPerde || (perdeSn > 0 && gecen >= perdeSn * 1_000)) bosPerde = true;
      else perdeyiAyarla(false);
    };
    const gizlenince = (): void => {
      if (tani) taniYaz("visibilitychange");
      if (globalThis.document?.visibilityState === "hidden") {
        perdeyiAyarla(true);
        void kilit.yaz().catch(() => undefined);
        return;
      }
      // Dokunmatikte odak henüz dönmediyse perde odakla kalkar.
      if (dokunmatik && globalThis.document?.hasFocus?.() === false) return;
      donus();
    };
    const sayfaGidince = (e: Event): void => {
      if (tani) taniYaz(e.type);
      perdeyiAyarla(true);
    };
    const odakGitti = (): void => {
      if (tani) taniYaz("blur");
      if (dokunmatik) perdeyiAyarla(true);
    };
    const odakGeldi = (): void => {
      if (tani) taniYaz("focus");
      if (dokunmatik && globalThis.document?.visibilityState !== "hidden") donus();
    };
    // Sayfa geri geldi / çözüldü: görünürse kilit süresine bakılır.
    const geriGeldi = (e: Event): void => {
      if (tani) taniYaz(e.type);
      if (globalThis.document?.visibilityState !== "hidden") donus();
    };
    for (const o of ETKILESIM) globalThis.addEventListener(o, kur, { passive: true });
    globalThis.document?.addEventListener("visibilitychange", gizlenince);
    globalThis.addEventListener("pagehide", sayfaGidince);
    globalThis.document?.addEventListener("freeze", sayfaGidince);
    globalThis.addEventListener("blur", odakGitti);
    globalThis.addEventListener("focus", odakGeldi);
    globalThis.addEventListener("pageshow", geriGeldi);
    globalThis.document?.addEventListener("resume", geriGeldi);
    kur();
    return () => {
      if (zaman !== null) clearTimeout(zaman);
      if (bosZaman !== null) clearTimeout(bosZaman);
      for (const o of ETKILESIM) globalThis.removeEventListener(o, kur);
      globalThis.document?.removeEventListener("visibilitychange", gizlenince);
      globalThis.removeEventListener("pagehide", sayfaGidince);
      globalThis.document?.removeEventListener("freeze", sayfaGidince);
      globalThis.removeEventListener("blur", odakGitti);
      globalThis.removeEventListener("focus", odakGeldi);
      globalThis.removeEventListener("pageshow", geriGeldi);
      globalThis.document?.removeEventListener("resume", geriGeldi);
    };
  }, [kilit, sureDk]);

  if (kilit === undefined) return null;
  return (
    <div
      ref={perde}
      data-testid="gizlilik-perdesi"
      role="presentation"
      style={PERDE_KAPALI}
      className="fixed inset-0 z-[2147483647] items-center justify-center bg-[var(--pf-bg)]"
    >
      <img src="/icon.svg" alt={M.kilit.perde} width={96} height={96} className="h-24 w-24" />
    </div>
  );
}
